import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Camera, Loader2, Instagram, Music2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SmartImage from "./SmartImage";
import CategoryIcon from "./CategoryIcon";
import { CATEGORY_OPTIONS } from "../lib/categories";

const CUISINE_TAGS = CATEGORY_OPTIONS.slice(0, 12).map((c) => [c.value, c.name, c.icon]);

export default function EditCustomerProfileSheet({ open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const cp = user?.customer_profile || {};
  const [form, setForm] = useState({
    bio: cp.bio || "",
    avatar_url: cp.avatar_url || null,
    banner_url: cp.banner_url || null,
    banner_display_mode: cp.banner_display_mode || "smart_auto_fit",
    city: cp.city || "",
    favorite_cuisines: cp.favorite_cuisines || [],
    instagram: cp.instagram || "",
    tiktok: cp.tiktok || "",
  });
  const [username, setUsername] = useState(user?.username || "");
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "ok" | "taken" | "invalid" | "current"
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const cp2 = user?.customer_profile || {};
      setForm({
        bio: cp2.bio || "",
        avatar_url: cp2.avatar_url || null,
        banner_url: cp2.banner_url || null,
        banner_display_mode: cp2.banner_display_mode || "smart_auto_fit",
        city: cp2.city || "",
        favorite_cuisines: cp2.favorite_cuisines || [],
        instagram: cp2.instagram || "",
        tiktok: cp2.tiktok || "",
      });
      setUsername(user?.username || "");
      setUsernameStatus(null);
    }
  }, [open, user]);

  // Debounced username availability check
  useEffect(() => {
    if (!open) return;
    const h = (username || "").trim().toLowerCase();
    if (!h) { setUsernameStatus(null); return; }
    if (h === (user?.username || "").toLowerCase()) { setUsernameStatus("current"); return; }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/username/available", { params: { handle: h } });
        if (data.available) setUsernameStatus("ok");
        else setUsernameStatus(data.reason === "invalid_format" ? "invalid" : "taken");
      } catch (e) {
        setUsernameStatus("invalid");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [username, open, user]);

  const toggleCuisine = (key) => {
    setForm((f) => ({
      ...f,
      favorite_cuisines: f.favorite_cuisines.includes(key)
        ? f.favorite_cuisines.filter((k) => k !== key)
        : [...f.favorite_cuisines, key],
    }));
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, avatar_url: data.path }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, banner_url: data.path }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Banner upload failed");
    } finally {
      setUploadingBanner(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Save username first if it changed and is available
      const h = (username || "").trim().toLowerCase();
      if (h && h !== (user?.username || "").toLowerCase()) {
        if (usernameStatus !== "ok") {
          toast.error(usernameStatus === "taken" ? "Handle is taken" : "Invalid handle format");
          setSaving(false);
          return;
        }
        try {
          await api.post("/users/me/username", { username: h });
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't save handle");
          setSaving(false);
          return;
        }
      }
      await api.put("/users/me/profile", form);
      await refreshUser();
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md overflow-y-auto"
        data-testid="edit-profile-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-xl font-black tracking-tighter">Edit profile</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {/* Banner uploader (Phase F) */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest font-black">Banner</Label>
              {form.banner_url && (
                <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-0.5" data-testid="edit-profile-banner-mode">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, banner_display_mode: "smart_auto_fit" }))}
                    className={`px-3 h-7 rounded-full text-[10px] uppercase tracking-widest font-black transition-colors ${
                      form.banner_display_mode === "smart_auto_fit"
                        ? "bg-pink-500/20 text-pink-200"
                        : "text-zinc-400 hover:text-white"
                    }`}
                    data-testid="edit-profile-banner-mode-fit"
                  >
                    Auto Fit
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, banner_display_mode: "fill_crop" }))}
                    className={`px-3 h-7 rounded-full text-[10px] uppercase tracking-widest font-black transition-colors ${
                      form.banner_display_mode === "fill_crop"
                        ? "bg-pink-500/20 text-pink-200"
                        : "text-zinc-400 hover:text-white"
                    }`}
                    data-testid="edit-profile-banner-mode-fill"
                  >
                    Fill
                  </button>
                </div>
              )}
            </div>
            <label
              className="mt-1.5 relative block w-full aspect-[3/1] rounded-2xl bg-gradient-to-br from-pink-500/20 via-purple-500/15 to-cyan-500/15 border-2 border-dashed border-white/15 hover:border-pink-400 cursor-pointer overflow-hidden"
              data-testid="edit-profile-banner"
            >
              {uploadingBanner ? (
                <span className="absolute inset-0 grid place-items-center">
                  <Loader2 className="w-7 h-7 animate-spin text-pink-300" />
                </span>
              ) : form.banner_url ? (
                <SmartImage
                  src={fileUrl(form.banner_url)}
                  alt="banner preview"
                  mode={form.banner_display_mode}
                  className="w-full h-full"
                  testid="edit-profile-banner-preview"
                />
              ) : (
                <span className="absolute inset-0 grid place-items-center text-zinc-300 text-xs font-bold">
                  <span className="inline-flex items-center gap-1.5"><Camera className="w-4 h-4" /> Upload cover image</span>
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleBanner}
                className="hidden"
                data-testid="edit-profile-banner-input"
              />
            </label>
            {form.banner_url && (
              <p className="mt-1 text-[10px] text-zinc-500">
                {form.banner_display_mode === "smart_auto_fit"
                  ? "Auto Fit — the full image shows with a blurred backdrop filling any empty space."
                  : "Fill — the image covers the banner and edges may be cropped."}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <label
              className="relative w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-white/15 hover:border-pink-400 cursor-pointer overflow-hidden grid place-items-center"
              data-testid="edit-profile-avatar"
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 animate-spin text-pink-300" />
              ) : form.avatar_url ? (
                <img src={fileUrl(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-6 h-6 text-zinc-400" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatar}
                className="hidden"
                data-testid="edit-profile-avatar-input"
              />
            </label>
            <p className="text-xs text-zinc-400">Tap to upload a profile picture.</p>
          </div>

          {/* Username (Phase F) */}
          <div className="space-y-2">
            <Label className="text-zinc-300">Handle</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm font-bold">@</span>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                placeholder="yourhandle"
                className="h-11 bg-white/5 border-white/10 rounded-2xl text-white pl-7"
                data-testid="edit-profile-username"
                maxLength={30}
              />
            </div>
            {usernameStatus === "checking" && (
              <p className="text-[10px] text-zinc-500" data-testid="edit-profile-username-status">Checking…</p>
            )}
            {usernameStatus === "ok" && (
              <p className="text-[10px] text-emerald-300 font-bold" data-testid="edit-profile-username-status">✓ Available</p>
            )}
            {usernameStatus === "current" && (
              <p className="text-[10px] text-zinc-500" data-testid="edit-profile-username-status">This is your current handle.</p>
            )}
            {usernameStatus === "taken" && (
              <p className="text-[10px] text-red-300 font-bold" data-testid="edit-profile-username-status">Taken — try another.</p>
            )}
            {usernameStatus === "invalid" && (
              <p className="text-[10px] text-red-300 font-bold" data-testid="edit-profile-username-status">3–30 chars, a-z / 0-9 / . / _ only.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Bio</Label>
            <Textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="Tell folks a bit about yourself."
              className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[80px]"
              data-testid="edit-profile-bio"
              maxLength={300}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">City</Label>
            <Input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Brooklyn, NY"
              className="h-11 bg-white/5 border-white/10 rounded-2xl text-white"
              data-testid="edit-profile-city"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-1"><Instagram className="w-3.5 h-3.5" /> Instagram</Label>
            <Input
              value={form.instagram}
              onChange={(e) => setForm({ ...form, instagram: e.target.value })}
              placeholder="@yourhandle"
              className="h-11 bg-white/5 border-white/10 rounded-2xl text-white"
              data-testid="edit-profile-instagram"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300 flex items-center gap-1"><Music2 className="w-3.5 h-3.5" /> TikTok</Label>
            <Input
              value={form.tiktok}
              onChange={(e) => setForm({ ...form, tiktok: e.target.value })}
              placeholder="@yourhandle"
              className="h-11 bg-white/5 border-white/10 rounded-2xl text-white"
              data-testid="edit-profile-tiktok"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Favorite cuisines</Label>
            <div className="flex flex-wrap gap-1.5">
              {CUISINE_TAGS.map(([key, label, iconSlug]) => {
                const on = form.favorite_cuisines.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleCuisine(key)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                      on
                        ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                        : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
                    }`}
                    data-testid={`edit-profile-cuisine-${key}`}
                  >
                    <CategoryIcon slug={iconSlug} size={12} strokeWidth={2.2} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
            data-testid="edit-profile-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
